/**
 * The JSONPath-ish selector grammar for persistent-agent watches: project an
 * MCP tool response down to the subtree worth watching, so noisy siblings
 * (server timestamps, request ids) don't register as changes and the fact
 * lane carries only the selected data.
 *
 * Deliberately a SAFE SUBSET — dot paths, numeric indices, and `[*]` mapping.
 * No filters, no scripts, no recursive descent: selection must be
 * deterministic and cheap because the host evaluates it on every poll.
 *
 * Grammar (leading `$` / `$.` optional):
 *   selector  = segment ("." segment)*
 *   segment   = name index* | index+          (bare index chains allowed at root)
 *   name      = [A-Za-z0-9_-]+
 *   index     = "[" digits "]" | "[*]"
 *
 * Examples: `drones`, `$.data.events`, `drones[*].status`, `[0].id`,
 * `fleet.units[*].position.lat`.
 *
 * `[*]` maps over an array and applies the REST of the path per element
 * (`drones[*].status` → `["ok","low", …]`). Any traversal miss (wrong type,
 * absent key, index out of range) yields `undefined` for that branch — a
 * valid projection, not an error, because the field may appear later.
 */
const segmentRe = /^([A-Za-z0-9_-]+)?((?:\[(?:\d+|\*)\])*)$/;
const indexRe = /\[(\d+|\*)\]/g;
/**
 * Parse a selector into segments, or null when the string is not valid
 * selector grammar (the API rejects such input; the host never sees it).
 */
export function parseWatchSelector(selector) {
    let body = selector.trim();
    if (body === '' || body.length > 500)
        return null;
    if (body === '$')
        return [];
    if (body.startsWith('$.'))
        body = body.slice(2);
    else if (body.startsWith('$['))
        body = body.slice(1);
    if (body === '')
        return null;
    const segments = [];
    for (const part of body.split('.')) {
        const m = segmentRe.exec(part);
        // A segment must contain SOMETHING (empty part = `..` or trailing dot).
        if (!m || (!m[1] && !m[2]))
            return null;
        const indices = [];
        if (m[2]) {
            for (const idx of m[2].matchAll(indexRe)) {
                indices.push(idx[1] === '*' ? null : Number(idx[1]));
            }
        }
        segments.push({ ...(m[1] ? { key: m[1] } : {}), indices });
    }
    return segments;
}
/**
 * Apply parsed segments to a value. Misses yield `undefined` (per branch
 * under `[*]`), never throw.
 */
export function applyWatchSelector(value, segments) {
    return walk(value, segments, 0, 0);
}
function walk(value, segments, seg, idx) {
    const current = segments[seg];
    if (!current)
        return value;
    // Resolve the object key once, before its index chain.
    if (idx === 0 && current.key !== undefined) {
        if (Array.isArray(value)) {
            // Arrays expose one safe synthetic field so scorecards and watches can
            // express "there is work" without a separate aggregation language.
            if (current.key !== 'length')
                return undefined;
            value = value.length;
        }
        else {
            if (!value || typeof value !== 'object')
                return undefined;
            value = value[current.key];
        }
    }
    for (let i = idx; i < current.indices.length; i++) {
        const index = current.indices[i];
        if (index === undefined)
            break;
        if (!Array.isArray(value))
            return undefined;
        if (index === null) {
            // `[*]`: map the REST of the whole path over each element.
            return value.map((el) => walk(el, segments, seg, i + 1));
        }
        value = value[index];
    }
    return walk(value, segments, seg + 1, 0);
}
/**
 * Deterministic JSON for change hashing: object keys sorted recursively so
 * key-order churn from a remote server never registers as a change. This IS
 * the watch contract's canonical form — the host's poll tick and the API's
 * preview endpoint hash exactly this string (sha256), so a preview hash
 * matches what the tick will later compute. `undefined` members are dropped
 * (like JSON.stringify); a top-level `undefined` renders as `"undefined"`.
 * Browser-safe: pure string building, no crypto here.
 */
export function stableWatchStringify(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'undefined';
    if (Array.isArray(value))
        return `[${value.map((v) => stableWatchStringify(v)).join(',')}]`;
    const entries = Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, v]) => `${JSON.stringify(k)}:${stableWatchStringify(v)}`);
    return `{${entries.join(',')}}`;
}
/**
 * Paths (selector-grammar shaped, `$`-rooted) where two samples of the same
 * tool response differ — the "churn detector" behind watch previews: fields
 * that changed between two back-to-back polls with no real state change are
 * noise the selector should exclude. Arrays of different lengths report the
 * array path itself; primitive/type mismatches report the leaf. Capped so a
 * pathological response can't flood the caller.
 */
export function diffWatchPaths(a, b, limit = 200) {
    const out = [];
    walkDiff(a, b, '$', out, limit);
    return out;
}
function walkDiff(a, b, path, out, limit) {
    if (out.length >= limit)
        return;
    if (Object.is(a, b))
        return;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            out.push(path);
            return;
        }
        for (let i = 0; i < a.length; i++)
            walkDiff(a[i], b[i], `${path}[${i}]`, out, limit);
        return;
    }
    const aIsPlain = !!a && typeof a === 'object' && !Array.isArray(a);
    const bIsPlain = !!b && typeof b === 'object' && !Array.isArray(b);
    if (aIsPlain && bIsPlain) {
        const ar = a;
        const br = b;
        const keys = new Set([...Object.keys(ar), ...Object.keys(br)]);
        for (const key of keys) {
            walkDiff(ar[key], br[key], path === '$' ? `$.${key}` : `${path}.${key}`, out, limit);
        }
        return;
    }
    out.push(path);
}
//# sourceMappingURL=watch-selector.js.map